import Module from '../core/module.js';
import IconUtils, { registerIcons, S } from '../ui/icons.js';

registerIcons({
  heading: S('<path d="M4 12h8"/><path d="M4 18V6"/><path d="M12 18V6"/><path d="m17 12 3-2v8"/>'),
  'list-bullet': S('<line x1="8" x2="21" y1="6" y2="6"/><line x1="8" x2="21" y1="12" y2="12"/><line x1="8" x2="21" y1="18" y2="18"/><circle cx="3.5" cy="6" r="1" fill="currentColor" stroke="none"/><circle cx="3.5" cy="12" r="1" fill="currentColor" stroke="none"/><circle cx="3.5" cy="18" r="1" fill="currentColor" stroke="none"/>'),
  'list-ordered': S('<line x1="10" x2="21" y1="6" y2="6"/><line x1="10" x2="21" y1="12" y2="12"/><line x1="10" x2="21" y1="18" y2="18"/><path d="M4 6h1v4"/><path d="M4 10h2"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/>'),
  table: S('<path d="M12 3v18"/><rect width="18" height="18" x="3" y="3" rx="2"/><path d="M3 9h18"/><path d="M3 15h18"/>'),
  code: S('<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>'),
  'code-view': S('<path d="m18 16 4-4-4-4"/><path d="m6 8-4 4 4 4"/><path d="m14.5 4-5 16"/>'),
  'font-family': S('<polyline points="4 7 4 4 20 4 20 7"/><line x1="9" x2="15" y1="20" y2="20"/><line x1="12" x2="12" y1="4" y2="20"/>'),
  'horizontal-rule': S('<path d="M5 12h14"/>')
});

/**
 * Slash command menu.
 *
 * Type "/" at the start of a block (or after whitespace) to open a quick menu
 * of block commands. Filter by typing, navigate with ↑/↓, choose with Enter,
 * dismiss with Esc. Selecting a command removes the typed "/query" and applies
 * the block transform.
 */
export default class SlashMenu extends Module {
  constructor(editor, options = {}) {
    super(editor, options);
    this.isOpen = false;
    this.activeIndex = 0;
    this.query = '';
    this.filtered = [];
    this.commands = this.buildCommands();
    this.buildMenu();
    this.bindEvents();
  }

  buildCommands() {
    const ed = this.editor;
    return [
      { id: 'h1', label: 'Heading 1', hint: 'Big section heading', icon: 'heading', run: () => ed.setBlockType('h1') },
      { id: 'h2', label: 'Heading 2', hint: 'Medium heading', icon: 'heading', run: () => ed.setBlockType('h2') },
      { id: 'h3', label: 'Heading 3', hint: 'Small heading', icon: 'heading', run: () => ed.setBlockType('h3') },
      { id: 'ul', label: 'Bullet list', hint: 'Unordered list', icon: 'list-bullet', run: () => ed.setBlockType('ul') },
      { id: 'ol', label: 'Numbered list', hint: 'Ordered list', icon: 'list-ordered', run: () => ed.setBlockType('ol') },
      { id: 'quote', label: 'Quote', hint: 'Blockquote', icon: 'code', run: () => ed.setBlockType('blockquote') },
      { id: 'code', label: 'Code block', hint: 'Preformatted code', icon: 'code-view', run: () => ed.setBlockType('pre') },
      { id: 'hr', label: 'Divider', hint: 'Horizontal rule', icon: 'horizontal-rule', run: () => ed.insertHorizontalRule() },
      { id: 'table', label: 'Table', hint: '3×3 table', icon: 'table', run: () => this.insertTable() },
      { id: 'p', label: 'Text', hint: 'Plain paragraph', icon: 'font-family', run: () => ed.setBlockType('p') }
    ];
  }

  insertTable() {
    const Table = this.editor.registry.get('formats/table');
    if (Table && typeof Table.createTableElement === 'function' && typeof this.editor.insertBlock === 'function') {
      this.editor.insertBlock(Table.createTableElement(3, 3));
    }
  }

  buildMenu() {
    const menu = document.createElement('div');
    menu.className = 'yjd-slash-menu';
    menu.setAttribute('role', 'listbox');
    menu.style.display = 'none';
    this.menu = menu;
    document.body.appendChild(menu);
  }

  bindEvents() {
    this._onInput = () => this.handleInput();
    this.editor.editor.addEventListener('input', this._onInput);

    // Keyboard interaction while open (capture so we beat other handlers).
    this._onKeydown = (e) => {
      if (!this.isOpen) return;
      const handled = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape'].includes(e.key);
      if (handled) { e.preventDefault(); e.stopPropagation(); }
      if (e.key === 'ArrowDown') this.move(1);
      else if (e.key === 'ArrowUp') this.move(-1);
      else if (e.key === 'Enter') this.choose(this.activeIndex);
      else if (e.key === 'Escape') this.close();
    };
    this.editor.editor.addEventListener('keydown', this._onKeydown, true);

    this._onDocPointer = (e) => {
      if (this.isOpen && !this.menu.contains(e.target)) this.close();
    };
    document.addEventListener('pointerdown', this._onDocPointer, true);
  }

  handleInput() {
    const sel = window.getSelection();
    if (!sel || !sel.isCollapsed || !sel.rangeCount) return this.close();
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return this.close();

    const before = node.textContent.slice(0, range.startOffset);
    const m = before.match(/(?:^|\s)\/([^\s/]*)$/);
    if (!m) return this.close();

    this.query = m[1];
    this.slashNode = node;
    this.slashStart = range.startOffset - this.query.length - 1; // index of "/"
    const q = this.query.toLowerCase();
    this.filtered = this.commands.filter(c =>
      c.label.toLowerCase().includes(q) ||
      c.id.toLowerCase().includes(q) ||
      (c.hint || '').toLowerCase().includes(q));
    if (!this.filtered.length) return this.close();
    this.activeIndex = 0;
    this.render();
    this.open(range);
  }

  open(range) {
    this.isOpen = true;
    this.menu.style.display = 'block';
    this._applyTheme();
    // Position below the caret.
    const rect = range.getBoundingClientRect();
    const x = rect.left || (range.startContainer.parentElement || this.editor.editor).getBoundingClientRect().left;
    const y = rect.bottom || rect.top;
    this.menu.style.left = `${Math.round(x + window.scrollX)}px`;
    this.menu.style.top = `${Math.round(y + window.scrollY + 6)}px`;
    // Flip up if off the bottom.
    const mh = this.menu.offsetHeight;
    if (rect.bottom + mh + 8 > window.innerHeight) {
      this.menu.style.top = `${Math.round(rect.top + window.scrollY - mh - 6)}px`;
    }
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.menu.style.display = 'none';
  }

  /**
   * Copy the editor's --rte-* theme vars onto the portaled menu so it matches
   * the editor (dark mode + custom app theming), since it lives on <body>.
   */
  _applyTheme() {
    const root = this.editor.wrapper || this.editor.root;
    if (!root) return;
    const cs = getComputedStyle(root);
    ['--rte-bg', '--rte-chrome-2', '--rte-ink', '--rte-muted', '--rte-border', '--rte-accent', '--rte-accent-ink', '--rte-accent-weak', '--rte-radius-md', '--rte-shadow']
      .forEach((v) => { const val = cs.getPropertyValue(v); if (val) this.menu.style.setProperty(v, val.trim()); });
  }

  move(delta) {
    this.activeIndex = (this.activeIndex + delta + this.filtered.length) % this.filtered.length;
    this.render();
  }

  render() {
    this.menu.innerHTML = '';
    this.filtered.forEach((cmd, i) => {
      const item = document.createElement('button');
      item.type = 'button';
      item.className = 'yjd-slash-item' + (i === this.activeIndex ? ' active' : '');
      item.setAttribute('role', 'option');
      item.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false');

      const icon = document.createElement('span');
      icon.className = 'yjd-slash-icon';
      icon.innerHTML = IconUtils.getIcon(cmd.icon) || '';

      const text = document.createElement('span');
      text.className = 'yjd-slash-text';
      text.innerHTML = `<span class="yjd-slash-label">${cmd.label}</span><span class="yjd-slash-hint">${cmd.hint}</span>`;

      item.append(icon, text);
      // pointerdown (not click) so the editor selection isn't lost first.
      item.addEventListener('pointerdown', (e) => { e.preventDefault(); this.choose(i); });
      this.menu.appendChild(item);
    });
  }

  choose(index) {
    const cmd = this.filtered[index];
    if (!cmd) return this.close();

    // Remove the typed "/query" then run the command.
    try {
      const node = this.slashNode;
      const sel = window.getSelection();
      const del = document.createRange();
      del.setStart(node, this.slashStart);
      del.setEnd(node, this.slashStart + this.query.length + 1);
      del.deleteContents();
      const caret = document.createRange();
      caret.setStart(node, this.slashStart);
      caret.collapse(true);
      sel.removeAllRanges();
      sel.addRange(caret);
    } catch (e) { /* node moved; run anyway */ }

    this.close();
    this.editor.focus();
    cmd.run(this.editor);
  }

  destroy() {
    this.editor.editor.removeEventListener('input', this._onInput);
    this.editor.editor.removeEventListener('keydown', this._onKeydown, true);
    document.removeEventListener('pointerdown', this._onDocPointer, true);
    if (this.menu && this.menu.parentNode) this.menu.parentNode.removeChild(this.menu);
    super.destroy();
  }
}
